import { useState, useEffect, ChangeEvent, useRef, useCallback } from 'react';

function useSearch<T extends Record<string, any>>(data: T[]) {
    const [searchText, setSearchText] = useState<string>('');
    const [filteredData, setFilteredData] = useState<T[]>(data);
    const prevDataRef = useRef<T[]>(data);
    const prevSearchTextRef = useRef<string>('');

    // Use a single effect to handle both data changes and search filtering
    useEffect(() => {
        const lowercasedSearchText = searchText.toLowerCase();
        const searchTextChanged = prevSearchTextRef.current !== searchText;
        const dataChanged = prevDataRef.current !== data;

        // Update refs
        if (dataChanged) {
            prevDataRef.current = data;
        }
        if (searchTextChanged) {
            prevSearchTextRef.current = searchText;
        }

        // Only update filteredData if something actually changed
        if (searchTextChanged || dataChanged) {
            if (lowercasedSearchText === '') {
                // Only update if data actually changed to prevent infinite loops
                if (dataChanged) {
                    setFilteredData(data);
                }
            } else {
                // Always filter when search text exists
                const newFiltered = data.filter(item =>
                    // Iterate over each value in the item object
                    Object.values(item).some(value =>
                        // Convert value to string and check if it includes the search text
                        String(value || '').toLowerCase().includes(lowercasedSearchText)
                    )
                );
                setFilteredData(newFiltered);
            }
        }
    }, [searchText, data]); // Only depend on searchText and data

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const resetSearch = useCallback(() => {
        setSearchText('');
    }, []);

    return { searchText, filteredData, handleSearchChange, setSearchText, resetSearch };
}

export default useSearch;